import * as THREE from 'three';
import { mapObjIndexed } from 'ramda';
import { get, getAll, getWorldTransform, set, getNodeBoundingBox } from '../scene';
import CONSTANTS from './physicsConstants';
import triangulateMesh from '../../polyMesh/geometric/operator/triangulateMesh';

// re-usable threeJS matrix decompose targets
const translation = new THREE.Vector3();
const rotation = new THREE.Quaternion();
const scale = new THREE.Vector3();

function initWorld(store) {
  const collisionConfiguration = new Ammo.btDefaultCollisionConfiguration();
  const dispatcher = new Ammo.btCollisionDispatcher(collisionConfiguration);
  const overlappingPairCache = new Ammo.btDbvtBroadphase();
  const solver = new Ammo.btSequentialImpulseConstraintSolver();
  const dynamicsWorld = new Ammo.btDiscreteDynamicsWorld(dispatcher, overlappingPairCache, solver, collisionConfiguration);

  let gravity = get(store, { plug: 'Player', property: 'gravity' });
  if (!gravity) gravity = new THREE.Vector3(0, -9.8, 0);
  dynamicsWorld.setGravity(new Ammo.btVector3(gravity.x, gravity.y, gravity.z));
  return dynamicsWorld;
}


function threeTransformToAmmo(threeTransform) {
  threeTransform.decompose(translation, rotation, scale);
  const ammoTranslation = new Ammo.btVector3(translation.x, translation.y, translation.z);
  const ammoRotation = new Ammo.btQuaternion(rotation.x, rotation.y, rotation.z, rotation.w);
  return new Ammo.btTransform(ammoRotation, ammoTranslation);
}

function ammoTransformToThree(ammoTransform) {
  const ammoTranslation = ammoTransform.getOrigin();
  const ammoRotation = ammoTransform.getRotation();

  const threeTranslation = new THREE.Vector3(ammoTranslation.x(), ammoTranslation.y(), ammoTranslation.z());
  const threeRotation = new THREE.Quaternion(ammoRotation.x(), ammoRotation.y(), ammoRotation.z(), ammoRotation.w());
  const threeScale = new THREE.Vector3(1, 1, 1);
  const threeTransform = new THREE.Matrix4();

  threeTransform.compose(threeTranslation, threeRotation, threeScale);
  return threeTransform;
}


function createConvexHullFromPolyMesh(polyMesh) {
  const shape = new Ammo.btConvexHullShape();
  const verts = polyMesh.positions.values;

  const tmpVec3 = new THREE.Vector3();
  for (let i = 0; i < verts.length; i++) {
    verts.getAt(i, tmpVec3);
    const pt = new Ammo.btVector3(tmpVec3.x, tmpVec3.y, tmpVec3.z);
    shape.addPoint(pt);
  }
  return shape;
}


function createCollisionMeshFromPolyMesh(polyMesh, store, id) {
  // try to get already-triangulated mesh from ThreeJS, otherwise create new triangulation to use
  const threeMesh = store.getTranslator().getThreeObject(id);
  let triMesh = null;
  if (threeMesh && threeMesh.userData && threeMesh.userData.triangulation) {
    triMesh = threeMesh.userData.triangulation.mesh;
  }
  if (!triMesh) {
    triMesh = triangulateMesh(polyMesh);
  }

  const verts = triMesh.positions.values;
  const indices = triMesh.positions.faceValueIndices;

  const mesh = new Ammo.btTriangleMesh(true, true);

  const tmpVec3a = new THREE.Vector3();
  const tmpVec3b = new THREE.Vector3();
  const tmpVec3c = new THREE.Vector3();
  for (let i = 0; i < indices.length; i+=3) {
    verts.getAt(i, tmpVec3a);
    verts.getAt(i+1, tmpVec3b);
    verts.getAt(i+2, tmpVec3c);
    const ptA = new Ammo.btVector3(tmpVec3a.x, tmpVec3a.y, tmpVec3a.z);
    const ptB = new Ammo.btVector3(tmpVec3b.x, tmpVec3b.y, tmpVec3b.z);
    const ptC = new Ammo.btVector3(tmpVec3c.x, tmpVec3c.y, tmpVec3c.z);

    mesh.addTriangle(ptA, ptB, ptC, false);
  }

  const shape = new Ammo.btBvhTriangleMeshShape(mesh, true, true);
  return shape;
}


function createBoundingBoxShapeFromNode(nodeID, bbox) {
  const halfExtents = new Ammo.btVector3((bbox.max.x - bbox.min.x) / 2, (bbox.max.y - bbox.min.y) / 2, (bbox.max.z - bbox.min.z) / 2);
  return new Ammo.btBoxShape(halfExtents);
}


/*
 * Creates the Dynamics World, and initializes the Kinematic and Dynamic Bodies
 */
export function initPhysics(store) {
  const kinematicBodies = {};
  const dynamicBodies = {};

  function createBodyWithProps(props, id) {
    let isKinematic = false;
    let isStatic = false;
    if (props.motionType === 'Kinematic') isKinematic = true;
    if (props.motionType === 'Static') isStatic = true;

    let colShape = null;
    let shapeLocalTransform = null;

    const worldTransformV2 = getWorldTransform(store, id);
    worldTransformV2.decompose(translation, rotation, scale);

    let shapeType = props.collisionShape;
  //  shapeType = 'Box';

    if (shapeType === 'Mesh') {
      const polyMesh = get(store, { id, evalPlug: 'PolyMesh', property: 'mesh' });
      if (polyMesh) {
        colShape = createCollisionMeshFromPolyMesh(polyMesh, store, id);
        colShape.setLocalScaling(new Ammo.btVector3(scale.x, scale.y, scale.z));
      }
    }
    else if (shapeType === 'Box') {
      const bbox = getNodeBoundingBox(store, id);
      colShape = createBoundingBoxShapeFromNode(id, bbox);
      const bbCenter = new THREE.Vector3((bbox.max.x + bbox.min.x) / 2, (bbox.max.y + bbox.min.y) / 2, (bbox.max.z + bbox.min.z) / 2);
      shapeLocalTransform = new THREE.Matrix4().getInverse(worldTransformV2, true);
      shapeLocalTransform.multiply(new THREE.Matrix4().makeTranslation(bbCenter.x, bbCenter.y, bbCenter.z));
      // bounding box already uses scaling?
    }
    else { // 'Convex'
      const polyMesh = get(store, { id, evalPlug: 'PolyMesh', property: 'mesh' });
      if (polyMesh) {
        colShape = createConvexHullFromPolyMesh(polyMesh);
        // convex hull is created from base geometry, so still need to scale appropriately
        colShape.setLocalScaling(new Ammo.btVector3(scale.x, scale.y, scale.z));

        // For now let's set 0 margins. Better quality/appearance at the cost of performance, but
        // the demos seem to run fine for now.
        colShape.setMargin(0.0);
      }
    }

    if (!colShape) {
      const nodeName = get(store, { id, property: 'name' });
      console.warn(`Could not create collision shape for '${nodeName}', defaulting to a unit sphere`);
      colShape = new Ammo.btSphereShape(1);
    }

    // some shapes need custom offset compensation relative to the node's transform to be positioned/oriented correctly
    if (shapeLocalTransform) {
      worldTransformV2.multiply(shapeLocalTransform);
    }

    worldTransformV2.decompose(translation, rotation, scale);

    const startTransform = new Ammo.btTransform();
    startTransform.setIdentity();
    startTransform.setOrigin(new Ammo.btVector3(translation.x, translation.y, translation.z));
    startTransform.setRotation(new Ammo.btQuaternion(rotation.x, rotation.y, rotation.z, rotation.w));

    const mass = (isKinematic || isStatic) ? 0 : props.mass;
    const isDynamic = (mass !== 0);
    const localInertia = new Ammo.btVector3(0, 0, 0);
    if (isDynamic) {
      colShape.calculateLocalInertia(mass, localInertia);
    }

    const myMotionState = new Ammo.btDefaultMotionState(startTransform);
    const rbInfo = new Ammo.btRigidBodyConstructionInfo(mass, myMotionState, colShape, localInertia);
    rbInfo.m_linearDamping = props.linearDamping;
    rbInfo.m_angularDamping = props.angularDamping;
    rbInfo.m_friction = props.friction;
    rbInfo.m_rollingFriction = props.rollingFriction;
    rbInfo.m_linearSleepingThreshold = props.linearSleepingThreshold;
    rbInfo.m_angularSleepingThreshold = props.angularSleepingThreshold;
    rbInfo.m_restitution = props.restitution;
    const body = new Ammo.btRigidBody(rbInfo);

    if (isKinematic) {
      body.setCollisionFlags(body.getCollisionFlags() | CONSTANTS.BODYFLAG_KINEMATIC_OBJECT);
      body.setActivationState(CONSTANTS.BODYSTATE_DISABLE_DEACTIVATION);
    }

    dynamicsWorld.addRigidBody(body);
    if (isKinematic) kinematicBodies[id] = { body, shapeLocalTransform };
    else if (!isStatic) dynamicBodies[id] = { body, shapeLocalTransform };
    // don't need to do process any updates with static bodies after they are added to the world
  }

  const dynamicsWorld = initWorld(store);

  const physicsNodes = getAll(store, { plug: 'Properties', properties: { name: 'RigidBodyProperties' } });

  mapObjIndexed(createBodyWithProps, physicsNodes);

  return { dynamicsWorld, kinematicBodies, dynamicBodies };
}


// Where do we want to perform this cleanup in the context of the V2 player?
function cleanupPhysics() {
  // Delete objects we created through |new|. We just do a few of them here, but you should do them all if you are not shutting down ammo.js
  // we'll free the objects in reversed order as they were created via 'new' to avoid the 'dead' object links
  // Ammo.destroy(dynamicsWorld);
  // Ammo.destroy(solver);
  // Ammo.destroy(overlappingPairCache);
  // Ammo.destroy(dispatcher);
  // Ammo.destroy(collisionConfiguration);
}


const internalSimulationInterval = 1/60;
const maxSubsteps = 4;

export function updatePhysics(store, delta, globalTime, dynamicsWorld, dynamicBodies, kinematicBodies) {
  // run simulation step
  // see http://bulletphysics.org/mediawiki-1.5.8/index.php/Stepping_The_World for param details
  // (actual deltaT in seconds, max # substeps, fixed minimum internal timestep)
  dynamicsWorld.stepSimulation(delta/1000, maxSubsteps, internalSimulationInterval);

  const trans = new Ammo.btTransform();
  let rotationPhysics = new Ammo.btQuaternion();
  const translationPhysics = new Ammo.btVector3();
  const threeQuat = new THREE.Quaternion();
  const euler = new THREE.Euler();

  // process kinematic bodies
  Object.keys(kinematicBodies).forEach((id) => {
    const body = kinematicBodies[id].body;
    const worldTransformV2 = getWorldTransform(store, id);

    const shapeLocalTransform = kinematicBodies[id].shapeLocalTransform;
    if (shapeLocalTransform) {
      worldTransformV2.premultiply(shapeLocalTransform);
    }

    worldTransformV2.decompose(translation, rotation, scale);

    const motionState = body.getMotionState();
    const worldTransformPhysics = body.getWorldTransform();

    translationPhysics.setX(translation.x);
    translationPhysics.setY(translation.y);
    translationPhysics.setZ(translation.z);

    rotationPhysics.setX(rotation.x);
    rotationPhysics.setY(rotation.y);
    rotationPhysics.setZ(rotation.z);
    rotationPhysics.setW(rotation.w);

    worldTransformPhysics.setOrigin(translationPhysics);
    worldTransformPhysics.setRotation(rotationPhysics);
    motionState.setWorldTransform(worldTransformPhysics);
  });

  // process dynamic bodies
  Object.keys(dynamicBodies).forEach((id) => {
    const body = dynamicBodies[id].body;
    body.getMotionState().getWorldTransform(trans);
    const worldTransformV2 = ammoTransformToThree(trans);

    // if the node has a parent transform, need to convert Bullet's world-space transform to
    // a local transform for this node
    const parentNode = store.get('sceneGraph').nodes[id].parent;
    const worldTransformV2Parent = getWorldTransform(store, parentNode);
    if (worldTransformV2Parent) {
      worldTransformV2.premultiply(new THREE.Matrix4().getInverse(worldTransformV2Parent, true));
    }

    // remove local shape transform
    const shapeLocalTransform = dynamicBodies[id].shapeLocalTransform;
    if (shapeLocalTransform) {
      worldTransformV2.premultiply(new THREE.Matrix4().getInverse(shapeLocalTransform, true));
    }

    worldTransformV2.decompose(translation, rotation, scale);


    store.dispatch(set({ id, plug: 'Transform', property: 'translation' },
      [translation.x, translation.y, translation.z]));

    euler.setFromQuaternion(rotation, 'ZYX');

    store.dispatch(set({ id, plug: 'Transform', property: 'rotation' },
      [THREE.Math.RAD2DEG * euler.x, THREE.Math.RAD2DEG * euler.y, THREE.Math.RAD2DEG * euler.z]));
  });
}



// WEBPACK FOOTER //
// ./src/modules/utils/physics.js